import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { whatsappBusinessAccountsApi, whatsappNumbersApi } from '@/lib/api/whatsapp-numbers.api';

import type {
  CreateWhatsAppBusinessAccountDto,
  StartWhatsAppOnboardingDto,
  UpdateWhatsAppBusinessAccountDto,
  WhatsAppAssignInboundNumberDto,
  WhatsAppOnboarding,
} from '@repo/shared-types';

const WHATSAPP_NUMBERS_QUERY_KEY = ['whatsapp-numbers'];
const WHATSAPP_BUSINESS_ACCOUNTS_QUERY_KEY = ['whatsapp-business-accounts'];
const onboardingKey = (id: string) => ['whatsapp-onboarding', id] as const;

export function useWhatsAppNumbers() {
  return useQuery({
    queryKey: WHATSAPP_NUMBERS_QUERY_KEY,
    queryFn: () => whatsappNumbersApi.list(),
  });
}

export function useSyncWhatsAppNumbers() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: () => whatsappNumbersApi.sync(),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: WHATSAPP_NUMBERS_QUERY_KEY });
    },
  });
}

export function useAssignInboundWhatsAppNumber() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      numberId,
      payload,
    }: {
      numberId: string;
      payload: WhatsAppAssignInboundNumberDto;
    }) => whatsappNumbersApi.assignInbound(numberId, payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: WHATSAPP_NUMBERS_QUERY_KEY });
      queryClient.invalidateQueries({ queryKey: ['configurations'] });
    },
  });
}

// ── Business accounts (WABAs) ─────────────────────────────────────

export function useWhatsAppBusinessAccounts() {
  return useQuery({
    queryKey: WHATSAPP_BUSINESS_ACCOUNTS_QUERY_KEY,
    queryFn: () => whatsappBusinessAccountsApi.list(),
  });
}

export function useCreateWhatsAppBusinessAccount() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (payload: CreateWhatsAppBusinessAccountDto) =>
      whatsappBusinessAccountsApi.create(payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: WHATSAPP_BUSINESS_ACCOUNTS_QUERY_KEY });
    },
  });
}

export function useUpdateWhatsAppBusinessAccount() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, payload }: { id: string; payload: UpdateWhatsAppBusinessAccountDto }) =>
      whatsappBusinessAccountsApi.update(id, payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: WHATSAPP_BUSINESS_ACCOUNTS_QUERY_KEY });
    },
  });
}

export function useSubscribeWhatsAppBusinessAccount() {
  return useMutation({
    mutationFn: (id: string) => whatsappBusinessAccountsApi.subscribe(id),
  });
}

export function useDeleteWhatsAppBusinessAccount() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => whatsappBusinessAccountsApi.remove(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: WHATSAPP_BUSINESS_ACCOUNTS_QUERY_KEY });
      queryClient.invalidateQueries({ queryKey: WHATSAPP_NUMBERS_QUERY_KEY });
    },
  });
}

// ── Onboarding (add number flow) ──────────────────────────────────

export function useStartOnboarding() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (dto: StartWhatsAppOnboardingDto) => whatsappNumbersApi.startOnboarding(dto),
    // Seed the progress query so the dialog shows the record without waiting for a fetch.
    onSuccess: (record) => {
      queryClient.setQueryData(onboardingKey(record.id), record);
    },
  });
}

export function useOnboarding(id: string | null) {
  const queryClient = useQueryClient();

  return useQuery({
    queryKey: onboardingKey(id ?? 'none'),
    queryFn: async () => {
      const record = await whatsappNumbersApi.getOnboarding(id!);
      if (record.status === 'done') {
        queryClient.invalidateQueries({ queryKey: WHATSAPP_NUMBERS_QUERY_KEY });
      }
      return record;
    },
    enabled: !!id,
    refetchInterval: (query) => {
      const status = query.state.data?.status;
      return status === 'running' || status === 'waiting_code' ? 2000 : false;
    },
  });
}

function useOnboardingAction(fn: (id: string, extra?: string) => Promise<WhatsAppOnboarding>) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, extra }: { id: string; extra?: string }) => fn(id, extra),
    onSuccess: (record) => {
      queryClient.setQueryData(onboardingKey(record.id), record);
      if (record.status === 'done') {
        queryClient.invalidateQueries({ queryKey: WHATSAPP_NUMBERS_QUERY_KEY });
      }
    },
  });
}

export function useSubmitOnboardingCode() {
  return useOnboardingAction((id, code) => whatsappNumbersApi.submitOnboardingCode(id, code ?? ''));
}

export function useResendOnboardingCode() {
  return useOnboardingAction((id) => whatsappNumbersApi.resendOnboardingCode(id));
}

export function useRetryOnboarding() {
  return useOnboardingAction((id) => whatsappNumbersApi.retryOnboarding(id));
}

export function useEnableWhatsAppCalling() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (numberId: string) => whatsappNumbersApi.enableCalling(numberId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: WHATSAPP_NUMBERS_QUERY_KEY });
    },
  });
}
